import { gql, useQuery } from '@apollo/client';
import MeIcon from 'assets/icons/menuIcon/me.svg?react';
import MapAttribution from 'components/Map/MapAttribution';
import MultilingualText from 'components/Multilingual/MultilingualText';
import { Title } from 'components/common/BrowserMetaData';
import CollapsibleFiltersButton from 'components/common/DropdownFilters/CollapsibleFiltersButton';
import LearningPathDropdownFilter from 'components/common/DropdownFilters/LearningPathDropdownFilter';
import SdgDropdownFilter from 'components/common/DropdownFilters/SdgDropdownFilter';
import InputSearchFilter, { SearchInputField } from 'components/common/InputSearchFilter';
import SdgIcon from 'components/community/SdgIcon';
import { LeavesIcon } from 'components/svg';
import MapLegendIcon from 'mdi-react/MapLegendIcon';
import ViewGridOutlineIcon from 'mdi-react/ViewGridOutlineIcon';
import { Map, Marker, Overlay, Point } from 'pigeon-maps';
import { maptiler } from 'pigeon-maps/providers';
import React, { FC, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Card, Collapse, Modal } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { CardImg } from 'reactstrap';
import { COMMUNITY_OVERVIEW_FRAGMENT, COMMUNITY_PROFILE_FRAGMENT } from 'services/community';
import {
  Community,
  CommunitySdg,
  LearningPath,
  SustainableDevelopmentGoal
} from 'services/graphqltypes';
import CommunityCard from '../../components/community/CommunityCard';
import { Colxx } from '../../components/custom/CustomBootstrap';
import IntlMessages from '../../helpers/IntlMessages';
import { getTranslatedString, setTabFromLocation } from '../../helpers/utils-typescript';
import PageHeadingWtButton from 'layouts/PageHeadingWtButton';

const LOAD_COMMUNITIES = gql`
  ${COMMUNITY_OVERVIEW_FRAGMENT}
  ${COMMUNITY_PROFILE_FRAGMENT}
  query LoadCommunities(
    $sdgId: UUID, 
    $learningPathId: UUID
  ) {
    communities(
      orderBy: TOTAL_CREDITS_WITH_SUBCOMMUNITIES_DESC, 
      filter: {
        type: { distinctFrom: SCHOOL_CLASS }
        isDisabled: {equalTo: false}, 
        communitySdgs: { some: { sdgId: { equalTo: $sdgId}}},
        communityLearningPaths: { some: { learningPathId: { equalTo: $learningPathId }}}
        }
    ) {
      totalCount
      nodes {
        ...communityOverviewFragment
        ...communityProfileFragment
      }
    }
  }
`;

export const CreateCommunityModal = ({
  displayCreateCommunityModal, setDisplayCreateCommunityModal }:
  { displayCreateCommunityModal: any, setDisplayCreateCommunityModal: any }): React.ReactElement => {
  const history = useHistory();
  return (
    <Modal
      show={displayCreateCommunityModal}
      onHide={() => setDisplayCreateCommunityModal(false)}
      className="rounded-modal">
      <Modal.Header closeButton className="mr-3 mt-3 p-0" />
      <div className="text-normal mt-0 pt-0 pl-2 pr-2" style={{ whiteSpace: 'pre-wrap' }}>
        <div className="text-center">
          <CardImg
            src="/assets/img/welcome_on_board_logo.png"
            alt="welcome on board image"
            className="w-sm-100 w-100"
          />
          <div className="mt-3">
            <div className="text-normal mb-1"><IntlMessages id="community.question-organization-type" /></div>
            <div className="text-normal mb-1"><IntlMessages id="community.question-sustainable-learning" /></div>
            <div className="text-normal mb-1"><IntlMessages id="community.question-accelerate-impact" /></div>
          </div>
          <div className="mt-3 mb-3">
            <h4 className="font-weight-bolder mb-3 ">
              <IntlMessages id="community.create-your-community" />
            </h4>
            <div className="text-normal text-left mb-3 pl-3 pr-3">
              <div className="row mb-1">
                <span className="col-1">✅</span> <span className="col-10">Create and manage activities</span>
              </div>
              <div className="row mb-1">
                <span className="col-1">✅</span> <span className="col-10">Access Best Practices Library (and share yours!)</span>
              </div>
              <div className="row mb-1">
                <span className="col-1">✅</span> <span className="col-10">Curate outdoor learning spaces and help your members grow their Bioregional Identity</span>
              </div>
            </div>
            <div>
              For more info head to the <a className="text-primary" href="/page/create-community/">ARK knowledge base article.</a>
            </div>
          </div>
        </div >
      </div>
      <div className='d-flex pl-4 pr-4'>
        <button className="mx-auto btn btn-outline-primary mb-3"
          onClick={() => { setDisplayCreateCommunityModal(false); }}
        >
          <IntlMessages id={'general.close'} />
        </button>
        <button className="mx-auto btn btn-yellow mb-3" onClick={() => { history.push('community/create-community'); }}>
          <IntlMessages id={'community.create-community'} />
        </button>
      </div>
    </Modal>
  );
};

interface MapMarkerAvatarProps {
  color: string;
  avatarUrl: string;
  radius: number;
}

/** Round map marker with a logo/avatar in the center */
export const MapMarkerAvatar: FC<MapMarkerAvatarProps> = ({ color, avatarUrl, radius }: MapMarkerAvatarProps) => {
  const markerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: radius,
    height: radius,
    borderRadius: '50%',
    backgroundColor: color,
    pointerEvents: 'auto',
  };

  return (
    <div style={markerStyle}>
      <img
        style={{ width: radius - 4, height: radius - 4 }} // 4px is the border of the marker
        src={avatarUrl || '/logo512.png'}
        className="rounded-circle"
        alt='Community logo'
      />
    </div>
  );
};

/** A single card that displays basic info about a community and can be clicked to enter the community details page */
const CommunityCardPortraitView = ({ community }: { community: Community }) => {
  return (
    <Card className='w-100'>
      <Link to={{
        pathname: `/community/${community.slug}`,
      }
      }>
        {/* cover picture url */}
        <div className="position-relative">
          <Card.Img
            loading='lazy'
            src={community.bannerImageUrl}
            alt="Community banner"
            className="responsive border-0 card-img-top clickable"
            style={{ aspectRatio: '20/9', objectFit: 'cover', maxHeight: '100%' }}
          />
        </div>
        <img
          src={community.avatarUrl}
          className="community-card-avatar rounded-circle"
          alt='Community logo'
        />

        <Card.Body className="py-3 px-3">
          <div
            className="text-center mt-4 h4 text-truncate onHoverYellow"
            style={{ maxWidth: '100%' }}>
            <MultilingualText value={community.name} />
          </div>

          <div className="d-flex flex-wrap align-items-center">
            <div className='d-flex mx-auto flex-fill mr-3 mb-2'>
              <LeavesIcon color='#60b6aa' strokeWidth={1} size={25} className='align-self-center' />
              <div className='align-self-center text-muted ml-1'>
                {community.totalCreditsWithSubcommunities > 1000 ? `${(community.totalCreditsWithSubcommunities / 1000).toPrecision(2)}k` : community.totalCreditsWithSubcommunities}
              </div>
            </div>

            <div className='d-flex mx-auto flex-fill mb-2 mr-2'>
              <MeIcon fill='#60b6aa' width={25} className='align-self-center' />
              <div className="align-self-center text-muted ml-1">
                {community.communityUsers.totalCount}
              </div>
            </div>

            <div className="ml-2 d-flex">
              {community?.communitySdgs?.nodes.map((communitySdg: CommunitySdg, index: number) => {
                return (
                  <SdgIcon
                    key={index}
                    iconId={communitySdg.sdg.number}
                    className="mr-1 mb-1 clickable"
                    size={40}
                  />
                );
              })}
            </div>

          </div>
        </Card.Body>
      </Link>
    </Card>
  );
};

type TitleAndFiltersProps = {
  setFilterByLearningPath: React.Dispatch<SetStateAction<string>>,
  filterByLearningPath: string,
  layout: 'grid' | 'map',
  setDebouncedSearchFieldValue: React.Dispatch<SetStateAction<string>>,
  setFilterBySdg: React.Dispatch<SetStateAction<SustainableDevelopmentGoal>>,
  filterBySdg: SustainableDevelopmentGoal
}

/** Page title + filters */
const TitleAndFilters = ({ setFilterByLearningPath, filterByLearningPath, layout,
  setDebouncedSearchFieldValue, setFilterBySdg, filterBySdg }: TitleAndFiltersProps) => {

  const intl = useIntl();
  const history = useHistory();

  const [showSearchField, setShowSearchField] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [displayCreateCommunityModal, setDisplayCreateCommunityModal] = useState<boolean>(false);


  const handleChangeFilterByLearningPath = (learningPath: LearningPath) => {
    setFilterByLearningPath(learningPath === null ? 'all' : learningPath.id);
    localStorage.setItem('filterByLearningPath', JSON.stringify(learningPath === null ? 'all' : learningPath.id));
  };

  return (
    <>
      {/* Title */}
      <Title sectionTitle={intl.formatMessage({ id: 'menu.communities' })} />

      {/* Heading */}
      <PageHeadingWtButton
        buttonHandler={() => setDisplayCreateCommunityModal(!displayCreateCommunityModal)}
        headingText={<IntlMessages id='menu.communities' />}
        desktopButtonText={<IntlMessages id="community.create-community" />}        
      />

      {/* Other buttons */}
      <div className='mb-2'>
        <div className="d-flex align-items-center justify-content-between">
          {/* Switch between map and list view */}
          <div className="d-flex">
            <ViewGridOutlineIcon
              className='clickable mr-2'
              size={30}
              color={layout === 'grid' ? '#60B6AA' : 'grey'}
              onClick={() => history.push({ ...location, hash: 'grid' })}
            />
            <MapLegendIcon
              className='clickable mr-2'
              size={30}
              color={layout === 'map' ? '#60B6AA' : 'grey'}
              onClick={() => history.push({ ...location, hash: 'map' })}
            />
          </div>

          {/* search input for screen bigger than 576px */}
          <InputSearchFilter
            setDebouncedSearchFieldValue={setDebouncedSearchFieldValue}
            setShowSearchField={setShowSearchField}
            showSearchField={showSearchField}
            setIsFiltersOpen={setIsFiltersOpen}
          />

          {/* Filters */}
          <div className="d-flex flex-grow-1 justify-content-end">
            {/* desktop */}
            <div className="d-none d-md-block">
              <div className="d-flex">
                <div className="d-flex">
                  <LearningPathDropdownFilter
                    filterByLearningPath={filterByLearningPath}
                    handleChangeFilterByLearningPath={handleChangeFilterByLearningPath}
                  />
                  <SdgDropdownFilter
                    filterBySdg={filterBySdg}
                    setFilterBySdg={setFilterBySdg}
                  />
                </div>
              </div>
            </div>
            {/* mobile toggle*/}
            <div className="d-md-none">
              <CollapsibleFiltersButton
                isFiltersOpen={isFiltersOpen}
                setIsFiltersOpen={setIsFiltersOpen}
                setShowSearchField={setShowSearchField}
              />
            </div>
          </div>
        </div>

        { /* Collapsible filters for mobile */}
        <Collapse in={isFiltersOpen || showSearchField}>
          <div id='filters-collapse'>
            {
              isFiltersOpen &&
              <div className="ml-auto pr-3 my-1" >
                <div className="d-flex">
                  <LearningPathDropdownFilter
                    filterByLearningPath={filterByLearningPath}
                    handleChangeFilterByLearningPath={handleChangeFilterByLearningPath}
                  />
                  <SdgDropdownFilter
                    filterBySdg={filterBySdg}
                    setFilterBySdg={setFilterBySdg}
                  />
                </div>
              </div>
            }

            {/* search input for screen smaller than 576px*/}
            {showSearchField &&
              <SearchInputField
                screenSize='mobile'
                setDebouncedSearchFieldValue={setDebouncedSearchFieldValue}
                showSearchField={showSearchField}
              />
            }
          </div>
        </Collapse>

      </div>

      <CreateCommunityModal
        displayCreateCommunityModal={displayCreateCommunityModal}
        setDisplayCreateCommunityModal={setDisplayCreateCommunityModal}
      />
    </>
  );
};

/** Renders list of communities. Used in /communities */
const CommunitiesList = (): React.ReactElement => {
  const location = useLocation();
  const { hash } = location;
  const allSdgs = { id: null, number: null } as unknown as SustainableDevelopmentGoal;
  const currentLang = useSelector((langState: any) => langState.settings.locale);

  /* */
  /* States */
  /* */
  const [filterByLearningPath, setFilterByLearningPath] = useState(null);
  const [filterBySdg, setFilterBySdg] = useState<SustainableDevelopmentGoal>(allSdgs);
  const [layout, setLayout] = useState<'grid' | 'map'>('grid');
  // const [mapCenterZoom, setMapCenterZoom] = useState({ center: [null, null] as Point, zoom: null });
  const [mapCenterZoom, setMapCenterZoom] = useState({ center: [48.19344298629141, 15.647543370723724] as Point, zoom: 11 });
  const [popoverPayload, setPopoverPayload] = useState<any>();
  const [debouncedSearchFieldValue, setDebouncedSearchFieldValue] = useState('');

  /* */
  /* Map/list view from hash */
  /* */
  useEffect(() => {
    if (hash) {
      setTabFromLocation(hash.toLowerCase(), ['grid', 'map'], setLayout);
    }
  }, [hash]);

  /* */
  /* Map provider */
  /* */
  const maptilerProvider = maptiler(import.meta.env.VITE_MAPTILER_API_KEY, 'topo');

  /* */
  /* Query*/
  /* */
  const { loading, error, data } = useQuery(LOAD_COMMUNITIES, {
    variables: {
      sdgId: filterBySdg?.id,
      learningPathId: filterByLearningPath !== 'all' ? filterByLearningPath : null,
    }
  });

  /* */
  /* Map center and zoom based on the shape polygon size */
  /* */
  // const setWidthHeightZoom = useCallback(() => {
  //   if (data?.communities) {
  //     // const mapWidth = node.clientWidth as number;
  //     // const mapHeight = node.clientHeight as number;
  //     // setMapInitialCenterZoom(viewport(data?.territory?.locationBoundingBox as any, [mapWidth, mapHeight]));
  //     setMapCenterZoom({
  //       'center': [
  //         15.647543370723724,
  //         48.19344298629141
  //       ],
  //       'zoom': 11
  //     });
  //   }
  // }, [data]);

  const communitiesToShow = useMemo(() => {
    if (loading || error) { return [] as Community[] }

    const communitiesFromQuery = data.communities.nodes;
    const searchedString = debouncedSearchFieldValue.toLowerCase().trim();
    const communitiesToShow: Community[] = communitiesFromQuery.filter((community: Community) => {
      // return the whole set of communities from query if there is no searchedString
      if (searchedString) {
        const nameIncludesSearchInput = getTranslatedString(community.name, currentLang).toLowerCase()
          .includes(searchedString);
        return nameIncludesSearchInput
      }
      return true
    });

    return communitiesToShow;
  }, [error, loading, data, debouncedSearchFieldValue, currentLang]);

  // Set filter values from localStorage
  useEffect(() => {
    localStorage.getItem('filterBySdg') &&
      setFilterBySdg(
        JSON.parse(localStorage.getItem('filterBySdg')) as unknown as SustainableDevelopmentGoal
      );
    localStorage.getItem('filterByLearningPath') &&
      setFilterByLearningPath(
        JSON.parse(localStorage.getItem('filterByLearningPath'))
      );
  }, []);

  if (loading) {
    return (
      <Fragment>
        <TitleAndFilters
          setFilterByLearningPath={setFilterByLearningPath}
          filterByLearningPath={filterByLearningPath}
          setFilterBySdg={setFilterBySdg}
          filterBySdg={filterBySdg}
          layout={layout}
          setDebouncedSearchFieldValue={setDebouncedSearchFieldValue}
        />
        <div className="loading" />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <TitleAndFilters
        setFilterByLearningPath={setFilterByLearningPath}
        filterByLearningPath={filterByLearningPath}
        setFilterBySdg={setFilterBySdg}
        filterBySdg={filterBySdg}
        layout={layout}
        setDebouncedSearchFieldValue={setDebouncedSearchFieldValue}
      />

      {/* Map view*/}
      {
        layout === 'map' &&
        <Fragment>
          <div
            // ref={setWidthHeightZoom}
            /* ref callback receives a reference to the rendered DOM element as its only argument. it is s called right after the attached DOM element is created, and again with null when the element is removed. https://julesblom.com/writing/ref-callback-use-cases */
            style={{ height: '76vh', borderRadius: '0.75rem', overflow: 'hidden' }}
            className={`mt-2 ${communitiesToShow.length === 0 && 'overlay-container'}`}>

            {/* Nothing to show text overlay */}
            {communitiesToShow.length === 0 &&
              <div className='overlay-text h1 text-center' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <IntlMessages id='general.loading-no-data' />
              </div>
            }

            {(mapCenterZoom.zoom > 0) &&
              <Map
                maxZoom={20}
                provider={maptilerProvider}
                attributionPrefix={false} // hide "Pigeon" text
                attribution={<MapAttribution />}
                dprs={[1, 2]}
                center={[mapCenterZoom.center[0], mapCenterZoom.center[1]]}
                zoom={mapCenterZoom.zoom}
                onBoundsChanged={({ center, zoom }) => {
                  setMapCenterZoom({ center, zoom });
                }}
                onClick={() => setPopoverPayload(null)} // close popover when clicking on map
              >

                {communitiesToShow.length > 0 && communitiesToShow.map((community: Community) => {
                  if (community.locationCoordinates?.x) {
                    return (
                      <Marker
                        offset={[0, -18]} // centers it -> half of the radius (makes the marker not travel on the map when zooming)
                        key={community.id}
                        width={36}
                        anchor={[community.locationCoordinates?.x, community.locationCoordinates?.y]}
                        onClick={({ anchor }) => {
                          setPopoverPayload({ payload: community, anchor });
                          setMapCenterZoom({ center: anchor, zoom: 15 });
                        }}
                      >
                        <MapMarkerAvatar
                          color='white'
                          avatarUrl={community.avatarUrl}
                          radius={36}
                        />
                      </Marker>
                    );
                  } else {
                    return null;
                  }
                })}

                { /* render popover with the community card */}
                {popoverPayload &&
                  <Overlay anchor={popoverPayload.anchor} offset={[150, -20]}>
                    <div style={{ width: '300px' }}>
                      <CommunityCardPortraitView community={popoverPayload.payload} />
                    </div>
                  </Overlay>
                }
              </Map>
            }
          </div>
        </Fragment>
      }

      {/* Grid view */}
      {
        layout === 'grid' &&
        <Row>
          <Colxx xxs="12">
            {communitiesToShow.length === 0
              ? <h1 className="mt-4 d-flex justify-content-center align-items-center">
                <IntlMessages id='general.loading-no-data' />
              </h1>
              : communitiesToShow.map((community: Community) => (
                <CommunityCard key={community.id} community={community} />
              ))
            }
          </Colxx>
        </Row>
      }
    </Fragment>
  );
};

export default CommunitiesList;
